import { useMutation } from "@tanstack/react-query";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  createSkillMutation,
  getSkillQueryKey,
  listSkillsQueryKey,
  type SkillCreate,
  type SkillUpdate,
  updateSkillMutation,
} from "@/api";
import type { SkillFormState } from "@/features/build/skills/SkillForm";
import type { DraftView } from "../draftModel";
import { useDraftActions } from "../useDraftActions";
import { useDraftRun } from "../useDraftRun";

/** Create (POST, linked to the chat) or Save (PUT) for a skill draft card. */
export function useSkillDraft(draft: DraftView) {
  const { t } = useTranslation("buildChat", { keyPrefix: "skill" });
  const navigate = useNavigate();
  const actions = useDraftActions();
  const create = useMutation(createSkillMutation());
  const update = useMutation(updateSkillMutation());
  const skillUuid = useRef<string | null>(draft.targetUuid);
  const body = draft.draft as Partial<SkillCreate>;

  const run = useDraftRun(
    draft.draftId,
    [
      draft.targetUuid
        ? {
            key: "save",
            label: t("save"),
            run: async () => {
              const path = { skill_uuid: draft.targetUuid ?? "" };
              await update.mutateAsync({ path, body: body as SkillUpdate, throwOnError: true });
            },
          }
        : {
            key: "create",
            label: t("create"),
            run: async () => {
              const result = await create.mutateAsync({
                body: { ...(body as SkillCreate), sourceSessionUuid: actions.sessionUuid },
                throwOnError: true,
              });
              skillUuid.current = result.data.uuid;
            },
          },
    ],
    {
      fallback: t("failed"),
      success: draft.targetUuid ? t("saved") : t("created"),
      entityUuid: () => skillUuid.current,
      invalidate: (uuid) => [listSkillsQueryKey(), getSkillQueryKey({ path: { skill_uuid: uuid } })],
    },
  );

  return {
    body,
    ...run,
    openInForm: draft.targetUuid
      ? undefined
      : () =>
          navigate("/build/skills/new", {
            state: { draft: body, sourceSessionUuid: actions.sessionUuid } satisfies SkillFormState,
          }),
  };
}
